import React, { useCallback, useEffect, useRef, useState } from "react";
import { BadgeCheck, Camera, PenLine, Undo2, FileJson, Copy } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SignaturePad } from "@/components/SignaturePad";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const MAX_TRIES = 3;

function FaceCam({ onPhoto, onFail }) {
  const videoRef = useRef(null); const streamRef = useRef(null);
  const [ready, setReady] = useState(false);
  const stop = useCallback(() => { streamRef.current?.getTracks().forEach((t) => t.stop()); streamRef.current = null; }, []);
  const start = useCallback(async () => {
    setReady(false); stop();
    try {
      const st = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" } });
      streamRef.current = st;
      if (videoRef.current) { videoRef.current.srcObject = st; await videoRef.current.play().catch(() => {}); }
      setReady(true);
    } catch { onFail("Não foi possível abrir a câmera."); }
  }, [stop, onFail]);
  useEffect(() => { start(); return () => stop(); }, [start, stop]);
  const capture = async () => {
    const v = videoRef.current; const c = document.createElement("canvas");
    c.width = v.videoWidth || 480; c.height = v.videoHeight || 360;
    c.getContext("2d").drawImage(v, 0, 0, c.width, c.height);
    const blob = await new Promise((r) => c.toBlob(r, "image/jpeg", 0.85));
    if (!blob || blob.size < 1000) { onFail("Leitura falhou (imagem vazia)."); return; }
    onPhoto(blob, c.toDataURL("image/jpeg", 0.6));
  };
  return (
    <div>
      <video ref={videoRef} playsInline autoPlay muted className="w-full rounded-xl border bg-slate-900" style={{ aspectRatio: "4/3" }} data-testid="ppe-face-video" />
      <div className="mt-2 flex gap-2">
        <Button onClick={capture} disabled={!ready} className="flex-1 bg-emerald-600 hover:bg-emerald-700" data-testid="ppe-face-capture"><Camera className="mr-1.5 h-4 w-4" /> {ready ? "Capturar rosto" : "Abrindo câmera..."}</Button>
        <Button variant="outline" onClick={start} data-testid="ppe-face-retry">Reabrir</Button>
      </div>
    </div>
  );
}

export function PpeConfirm({ row, onDone }) {
  const [open, setOpen] = useState(false);
  const [method, setMethod] = useState("biometria");
  const [tries, setTries] = useState(0);
  const [photo, setPhoto] = useState(null);
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const fail = useCallback((msg) => {
    toast.error(msg);
    setTries((t) => t + 1);
  }, []);
  useEffect(() => {
    if (tries >= MAX_TRIES && method === "biometria") { setMethod("assinatura"); toast.info("Contingência liberada: use a Assinatura em Tela."); }
  }, [tries]); // eslint-disable-line react-hooks/exhaustive-deps
  const locked2 = tries < MAX_TRIES;
  const submit = async () => {
    const fd = new FormData();
    fd.append("method", method); fd.append("biometric_attempts", String(tries));
    if (method === "biometria") { if (!photo) { toast.error("Capture o rosto do colaborador"); return; } fd.append("photo", photo.blob, "face.jpg"); }
    else { if (!sig) { toast.error("Colete a assinatura"); return; } fd.append("signature", sig); }
    setBusy(true);
    try {
      await api.post(`/ppe-deliveries/${row.id}/confirm`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Entrega confirmada e registrada de forma imutável"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };
  const opt = (k, icon, label, sub, disabled = false) => (
    <button type="button" disabled={disabled} onClick={() => setMethod(k)} data-testid={`ppe-method-${k}`}
      className={`flex flex-1 items-start gap-2 rounded-xl border p-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${method === k ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:bg-slate-50"}`}>
      {icon}<span><span className="block text-sm font-semibold text-slate-800">{label}</span><span className="text-xs text-slate-500">{sub}</span></span>
    </button>
  );
  return (
    <>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setOpen(true); setTries(0); setPhoto(null); setSig(null); setMethod("biometria"); }} title="Confirmar recebimento" data-testid={`ppe-confirm-btn-${row.id}`}>
        <BadgeCheck className="h-4 w-4 text-emerald-600" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-[560px]" data-testid="ppe-confirm-dialog">
          <DialogHeader><DialogTitle className="text-[#0B2545]">Confirmar entrega de EPI</DialogTitle></DialogHeader>
          <div className="rounded-lg bg-slate-50 p-3 text-sm">
            <p><b>Funcionário:</b> {row.employee_name}</p>
            <p><b>EPI:</b> {row.ppe_name} — CA {row.ca_number || "—"} — Lote {row.lot_number || "—"}</p>
          </div>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Método de validação</p>
          <div className="flex flex-col gap-2 sm:flex-row">
            {opt("biometria", <Camera className="mt-0.5 h-4 w-4 text-emerald-600" />, "1 - Biometria", "Foto facial no ato da entrega")}
            {opt("assinatura", <PenLine className="mt-0.5 h-4 w-4 text-[#134074]" />, "2 - Assinatura em Tela", locked2 ? `Libera após ${MAX_TRIES} falhas na biometria` : "Contingência liberada", locked2)}
          </div>
          {method === "biometria" ? (
            photo ? (
              <div><img src={photo.preview} alt="Captura" className="w-full rounded-xl border" data-testid="ppe-face-preview" />
                <Button variant="outline" className="mt-2 w-full" onClick={() => setPhoto(null)} data-testid="ppe-face-redo">Capturar novamente</Button></div>
            ) : <FaceCam onPhoto={(blob, preview) => setPhoto({ blob, preview })} onFail={fail} />
          ) : <SignaturePad value={sig} onChange={setSig} tablet testid="ppe-confirm-signature" label="Assinatura do colaborador" />}
          {tries > 0 && <p className="text-xs text-amber-700" data-testid="ppe-face-tries">Falhas de leitura: {tries}/{MAX_TRIES}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} disabled={busy} className="bg-[#0B2545] hover:bg-[#134074]" data-testid="ppe-confirm-submit">{busy ? "Registrando..." : "Confirmar entrega"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function PpeEstorno({ row, onDone }) {
  const { canManage } = useAuth();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  if (!canManage) return null;
  const submit = async () => {
    try {
      await api.post(`/ppe-deliveries/${row.id}/estorno`, { reason });
      toast.success("Estorno registrado — o registro original foi mantido como [ESTORNADO]"); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setReason(""); setOpen(true); }} title="Estornar" data-testid={`ppe-estorno-btn-${row.id}`}>
        <Undo2 className="h-4 w-4 text-rose-500" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[480px]" data-testid="ppe-estorno-dialog">
          <DialogHeader><DialogTitle className="text-[#0B2545]">Estornar entrega</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">O registro não é apagado: ele fica marcado como <b>[ESTORNADO]</b> e uma linha de contrapartida é criada, preservando assinatura/biometria para auditoria.</p>
          <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo do estorno (obrigatório)" data-testid="ppe-estorno-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={submit} className="bg-rose-600 hover:bg-rose-700" data-testid="ppe-estorno-submit">Confirmar estorno</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function EsocialView({ row }) {
  const [open, setOpen] = useState(false);
  const json = JSON.stringify(row.payload_esocial_pronto, null, 2);
  return (
    <>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setOpen(true)} title="eSocial S-2240 / Auditoria" data-testid={`ppe-esocial-btn-${row.id}`}>
        <FileJson className="h-4 w-4 text-[#134074]" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[88vh] overflow-y-auto sm:max-w-[620px]" data-testid="ppe-esocial-dialog">
          <DialogHeader><DialogTitle className="text-[#0B2545]">Auditoria e eSocial (S-2240)</DialogTitle></DialogHeader>
          <div className="space-y-1 text-xs text-slate-600">
            {(row.audit_log || []).map((l, i) => <p key={i} className="font-mono">{l}</p>)}
            <p className="break-all"><b>Hash de auditoria:</b> <span className="font-mono">{row.hash_biometrico_log}</span></p>
          </div>
          <pre className="max-h-80 overflow-auto rounded-lg bg-[#0B2545] p-3 text-[11px] text-emerald-200" data-testid="ppe-esocial-json">{json}</pre>
          <DialogFooter>
            <Button variant="outline" onClick={() => { navigator.clipboard?.writeText(json); toast.success("JSON copiado"); }} data-testid="ppe-esocial-copy"><Copy className="mr-1.5 h-4 w-4" /> Copiar JSON</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
